import { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import ReactMarkdown from 'react-markdown';
import AppHeader from './AppHeader';
import { useViewportHeight } from './hooks/useViewportHeight';
import { useLanguage } from './LanguageContext';
import { el as elStrings, en as enStrings } from './i18n/app';

const API = process.env.REACT_APP_API_URL || 'http://localhost:3000';

const MODULE_DEFS: Record<string, { title: string; description: string; color: string; icon: string }> = {
  'Project Charter':      { title: 'Project Charter',       description: 'Scope, objectives, KPIs',         color: '#4f46e5', icon: '📋' },
  'Kickoff Presentation': { title: 'Kickoff Presentation',  description: 'Εναρκτήρια παρουσίαση',           color: '#8b5cf6', icon: '🚀' },
  'Schedule':             { title: 'Schedule',              description: 'Gantt, milestones, WBS',          color: '#0ea5e9', icon: '📅' },
  'Budget':               { title: 'Budget',                description: 'Κόστος, MD, EVM',                 color: '#10b981', icon: '💰' },
  'Risk Management':      { title: 'Risk Management',       description: 'Register, matrix, actions',       color: '#ef4444', icon: '⚠️' },
  'Issues':               { title: 'Issues',                description: 'Log, priority, resolution',       color: '#f59e0b', icon: '🔧' },
  'Stakeholders':         { title: 'Stakeholders',          description: 'Register, engagement',            color: '#ec4899', icon: '🤝' },
  'Daily Log':            { title: 'Task Tracker',          description: '',                                color: '#8b5cf6', icon: '✅' },
  'Organization Chart':   { title: 'Organization Chart',    description: 'Roles, team, RACI',               color: '#06b6d4', icon: '👥' },
  'Scope':                { title: 'Scope / WBS',           description: 'Work Breakdown Structure',        color: '#059669', icon: '🗂️' },
  'Documents':            { title: 'Project Documents',     description: 'Plans, παραδοτέα, αρχεία',        color: '#6b7280', icon: '📁' },
  'Reporting':            { title: 'Reporting',             description: 'Αναφορές ανά ομάδα stakeholders', color: '#14b8a6', icon: '📊' },
  'AI Advisor':           { title: 'AI Advisor',            description: 'Insights, προτάσεις, ενέργειες', color: '#4f46e5', icon: '✦'  },
  'Transcripts':          { title: 'Transcripts',           description: 'Συναντήσεις, AI ανάλυση',        color: '#0ea5e9', icon: '🎙️' },
  'Meetings':             { title: 'Meetings',              description: 'Agenda ανά ομάδα, AI',           color: '#4f46e5', icon: '🗓️' },
  'Project Specs':        { title: 'Project Specs',         description: 'Στοιχεία, email, ρυθμίσεις',     color: '#14b8a6', icon: '⚙️' },
};

const MODULE_GROUPS = [
  { label: 'ΣΧΕΔΙΑΣΜΟΣ & ΕΚΚΙΝΗΣΗ',    cols: 2, planning: true,  ids: ['Project Charter', 'Kickoff Presentation'] },
  { label: 'ΕΚΤΕΛΕΣΗ & ΠΑΡΑΚΟΛΟΥΘΗΣΗ', cols: 5, planning: false, ids: ['Schedule', 'Budget', 'Risk Management', 'Issues', 'Stakeholders', 'Daily Log', 'Organization Chart', 'Scope', 'Documents', 'Reporting'] },
  { label: 'AI & ΕΓΓΡΑΦΑ',             cols: 4, planning: false, ids: ['AI Advisor', 'Transcripts', 'Meetings', 'Project Specs'] },
];

const FULL_ACCESS_ROLES = ['admin', 'pm', 'pmo'];

interface Message    { role: 'user' | 'assistant'; content: string; }
interface Permission { module: string; can_view: boolean; can_edit: boolean; }

// ─── Stats helpers (language-aware via s parameter) ────────────────────────────
type StatStrings = {
  statRisks: (n: number) => string;
  statCritical: (n: number) => string;
  statPhase: (n: number) => string;
  statSchedule: (phases: number, activities: number) => string;
  statOpenIssues: (n: number) => string;
  statOverdue: (n: number) => string;
  statMembers: (n: number) => string;
};

const getRiskStats = (data: any, s: StatStrings) => {
  const risks: any[] = data?.risks || [];
  if (!risks.length) return null;
  const critical = risks.filter((r: any) =>
    (r.probability === 'Υψηλή' || r.probability === 'High') &&
    (r.impact === 'Υψηλό' || r.impact === 'High')
  ).length;
  return {
    metric: null,
    badge: critical > 0 ? s.statCritical(critical) : null,
    badgeColor: critical > 0 ? 'red' : 'green',
    progress: null,
    stat: s.statRisks(risks.length),
  };
};

const getScheduleStats = (data: any, s: StatStrings) => {
  const phases: any[] = data?.phases || [];
  if (!phases.length) return null;
  const activities: any[] = data?.activities || [];
  return {
    metric: null,
    badge: null,
    badgeColor: 'blue',
    progress: null,
    stat: s.statSchedule(phases.length, activities.length),
  };
};

const getIssuesStats = (data: any, s: StatStrings) => {
  const issues: any[] = data?.issues || [];
  if (!issues.length) return null;
  const open = issues.filter((i: any) => i.status === 'open' || i.status === 'Ανοιχτό' || i.status === 'in_progress').length;
  return {
    metric: null,
    badge: open > 0 ? s.statOpenIssues(open) : null,
    badgeColor: 'amber',
    progress: null,
    stat: `${issues.length} issues`,
  };
};

const getBudgetStats = (_data: any) => null;

const getDailyLogStats = (tasks: any[], s: StatStrings) => {
  if (!tasks?.length) return null;
  const d = new Date();
  const today = `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
  const overdue = tasks.filter((t: any) => t.due_date && t.due_date < today && t.status?.toLowerCase() !== 'done').length;
  return {
    metric: null,
    badge: overdue > 0 ? s.statOverdue(overdue) : null,
    badgeColor: 'red',
    progress: null,
    stat: null,
  };
};

const getStakeholdersStats = (data: any, _s: StatStrings) => {
  const stakeholders: any[] = data?.stakeholders || [];
  if (!stakeholders.length) return null;
  return { metric: null, badge: null, badgeColor: 'gray', progress: null, stat: 'Register, engagement' };
};

// ─── Badge colors ─────────────────────────────────────────────────────────────
const BADGE_STYLES: Record<string, { bg: string; color: string }> = {
  red:    { bg: '#fee2e2', color: '#991b1b' },
  amber:  { bg: '#fef9c3', color: '#854d0e' },
  green:  { bg: '#dcfce7', color: '#166534' },
  gray:   { bg: '#f3f4f6', color: '#6b7280' },
  blue:   { bg: '#dbeafe', color: '#1d4ed8' },
  purple: { bg: '#ede9fe', color: '#5b21b6' },
};

// ─── Component ────────────────────────────────────────────────────────────────
const ProjectHub = ({ project, user, token, onBack, onSelectModule, onProfileEdit, onLogout, canViewAIAssistant, onProjectRenamed }: {
  project: any; user: any; token: string;
  onBack: () => void; onSelectModule: (module: string, openCreate?: boolean) => void;
  onProfileEdit: () => void; onLogout?: () => void; canViewAIAssistant?: boolean;
  onProjectRenamed?: (newName: string) => void;
}) => {
  const { language } = useLanguage();
  const L = language === 'en' ? enStrings : elStrings;

  const [chatOpen, setChatOpen] = useState(false);
  const [chatExpanded, setChatExpanded] = useState(false);
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [thinking, setThinking] = useState(false);
  const [permissions, setPermissions] = useState<Permission[]>([]);
  const [permissionsLoaded, setPermissionsLoaded] = useState(false);
  const [moduleData, setModuleData] = useState<Record<string, any>>({});
  const [editingName, setEditingName] = useState(false);
  const [nameInput, setNameInput] = useState(project.name);
  const [savingName, setSavingName] = useState(false);
  const nameInputRef = useRef<HTMLInputElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const headers = { Authorization: `Bearer ${token}` };
  const isTrueFullAccess = FULL_ACCESS_ROLES.includes(user.role);
  // PM has full module rights, but only within projects they're actually assigned to —
  // verified below, never assumed from role alone.
  const [isPmMember, setIsPmMember] = useState(false);
  const isFullAccess = isTrueFullAccess || (user.role === 'pm' && isPmMember);
  const showAIAssistant = isFullAccess || canViewAIAssistant === true;

  useEffect(() => {
    if (isTrueFullAccess) { setPermissionsLoaded(true); return; }
    if (user.role === 'pm') {
      axios.get(`${API}/projects/${project.id}/members`, { headers })
        .then(res => setIsPmMember((res.data || []).includes(user.id)))
        .catch(() => setIsPmMember(false))
        .finally(() => setPermissionsLoaded(true));
      return;
    }
    axios.get(`${API}/users/${user.id}/permissions/${project.id}`, { headers })
      .then(res => setPermissions(res.data || []))
      .catch(() => setPermissions([]))
      .finally(() => setPermissionsLoaded(true));
  }, [project.id, user.id]);

  useEffect(() => {
    const fetchStats = async () => {
      const mods = ['Risk Management', 'Schedule', 'Issues', 'Budget', 'Stakeholders'];
      const results = await Promise.allSettled(
        mods.map(m => axios.get(`${API}/modules/${project.id}/${encodeURIComponent(m)}`, { headers }).then(r => ({ m, data: r.data?.data || r.data })))
      );
      const stats: Record<string, any> = {};
      results.forEach(r => { if (r.status === 'fulfilled') stats[r.value.m] = r.value.data; });
      try {
        const dl = await axios.get(`${API}/daily-log/${project.id}`, { headers });
        stats['Daily Log'] = dl.data;
      } catch {}
      setModuleData(stats);
    };
    fetchStats();
  }, [project.id]);

  const getCardStats = (id: string) => {
    const d = moduleData[id];
    if (!d) return null;
    const s = L.projectHub;
    switch (id) {
      case 'Risk Management': return getRiskStats(d, s);
      case 'Schedule':        return getScheduleStats(d, s);
      case 'Issues':          return getIssuesStats(d, s);
      case 'Budget':          return getBudgetStats(d);
      case 'Stakeholders':    return getStakeholdersStats(d, s);
      case 'Daily Log':       return getDailyLogStats(Array.isArray(d) ? d : [], s);
      default: return null;
    }
  };

  const canView = (id: string) => isFullAccess || permissions.find(p => p.module === id)?.can_view === true;
  const canEdit = (id: string) => isFullAccess || permissions.find(p => p.module === id)?.can_edit === true;

  const saveProjectName = async () => {
    const trimmed = nameInput.trim().slice(0, 100);
    if (!trimmed || trimmed === project.name) { setEditingName(false); setNameInput(project.name); return; }
    setSavingName(true);
    try {
      await axios.patch(`${API}/projects/${project.id}/rename`, { name: trimmed }, { headers });
      project.name = trimmed;
      onProjectRenamed?.(trimmed);
    } catch { setNameInput(project.name); }
    finally { setSavingName(false); setEditingName(false); }
  };

  useEffect(() => {
    const greeting = { role: 'assistant' as const, content: L.projectHub.chatGreeting(project.name) };
    if (chatOpen && messages.length === 0) {
      setMessages([greeting]);
    } else if (chatOpen && messages.length === 1) {
      setMessages([greeting]);
    }
  }, [chatOpen, language]);

  useEffect(() => { messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);

  const sendMessage = async () => {
    if (!input.trim() || thinking) return;
    const msg = input.trim(); setInput('');
    setMessages(prev => [...prev, { role: 'user', content: msg }]);
    setThinking(true);
    try {
      const res = await axios.post(`${API}/advisor/${project.id}/chat`, { message: msg, history: messages.slice(-10), language }, { headers });
      setMessages(prev => [...prev, { role: 'assistant', content: res.data.reply }]);
    } catch { setMessages(prev => [...prev, { role: 'assistant', content: L.projectHub.chatError }]); }
    finally { setThinking(false); }
  };

  const viewportHeight = useViewportHeight();
  // Chat panel: compact by default, expandable for long answers. Height is capped so the
  // panel never runs under the app header on short screens.
  const chatWidth = chatExpanded ? 480 : 360;
  const chatHeight = `min(${chatExpanded ? 680 : 500}px, calc(${viewportHeight} - 190px))`;

  if (!permissionsLoaded) return (
    <div style={{ minHeight: viewportHeight, background: '#f5f4ef', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ color: '#9ca3af', fontSize: 14 }}>{L.projectHub.loading}</div>
    </div>
  );

  return (
    <div style={{ minHeight: viewportHeight, background: '#f5f4ef', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif' }}>

      <AppHeader user={user} onHome={onBack} onProfileEdit={onProfileEdit} onLogout={onLogout} />

      <div style={{ padding: '8px 24px 16px', maxWidth: 1200, margin: '0 auto' }}>

        {/* Breadcrumb */}
        <div style={{ fontSize: 12, color: '#9ca3af', marginBottom: 10 }}>
          <span style={{ color: '#4f46e5', cursor: 'pointer' }} onClick={onBack}>{L.projectHub.myProjects}</span>
          {' / '}{project.name}
        </div>

        {/* Project Header */}
        <div style={{ background: '#fff', borderRadius: 12, padding: '10px 16px', border: '1px solid #e5e7eb', marginBottom: 14, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            {editingName ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <input
                  ref={nameInputRef}
                  value={nameInput}
                  maxLength={100}
                  onChange={e => setNameInput(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') saveProjectName(); if (e.key === 'Escape') { setEditingName(false); setNameInput(project.name); } }}
                  onBlur={saveProjectName}
                  autoFocus
                  style={{ fontSize: 17, fontWeight: 600, color: '#111', border: '1.5px solid #4f46e5', borderRadius: 6, padding: '2px 8px', outline: 'none', fontFamily: 'inherit', width: 320, background: '#f8f9ff' }}
                />
                {savingName && <span style={{ fontSize: 12, color: '#9ca3af' }}>{L.projectHub.saving}</span>}
              </div>
            ) : (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <div style={{ fontSize: 22, fontWeight: 600, color: '#111' }}>{project.name}</div>
                {isFullAccess && (
                  <button
                    onClick={() => { setNameInput(project.name); setEditingName(true); }}
                    title={L.projectHub.editNameTitle}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af', fontSize: 13, padding: '2px 4px', borderRadius: 4, lineHeight: 1 }}
                    onMouseEnter={e => (e.currentTarget.style.color = '#4f46e5')}
                    onMouseLeave={e => (e.currentTarget.style.color = '#9ca3af')}
                  >✎</button>
                )}
              </div>
            )}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {(() => {
              const actions = ([['+ Issue', 'Issues'], ['+ Task', 'Daily Log'], ['+ Risk', 'Risk Management'], ['Upload', 'Documents']] as [string, string][])
                .filter(([, mod]) => canEdit(mod));
              return actions.map(([label, mod]) => (
                <button key={label} onClick={() => onSelectModule(mod, mod !== 'Documents')}
                  style={{ fontSize: 13, padding: '7px 16px', borderRadius: 999, border: '1px solid #e5e7eb', background: '#f9fafb', color: '#374151', cursor: 'pointer', fontFamily: 'inherit' }}
                  onMouseEnter={e => (e.currentTarget.style.background = '#f3f4f6')}
                  onMouseLeave={e => (e.currentTarget.style.background = '#f9fafb')}>
                  {label}
                </button>
              ));
            })()}
          </div>
        </div>

        {/* Module Groups */}
        {MODULE_GROUPS.map(group => {
          const visible = group.ids.filter(id => canView(id));
          if (!visible.length) return null;
          return (
            <div key={group.label} style={{ marginBottom: 12 }}>
              {/* Section header */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.08em', whiteSpace: 'nowrap' }}>
                  {group.label === 'ΣΧΕΔΙΑΣΜΟΣ & ΕΚΚΙΝΗΣΗ' ? L.projectHub.groupPlanning
                    : group.label === 'ΕΚΤΕΛΕΣΗ & ΠΑΡΑΚΟΛΟΥΘΗΣΗ' ? L.projectHub.groupExecution
                    : L.projectHub.groupAI}
                </div>
                <div style={{ flex: 1, height: 1, background: '#e5e7eb' }} />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: `repeat(${group.cols}, 1fr)`, gap: 7 }}>
                {visible.map(id => {
                  const mod = MODULE_DEFS[id];
                  if (!mod) return null;
                  const stats = getCardStats(id);
                  const badge = stats?.badge ? (BADGE_STYLES[stats.badgeColor] || BADGE_STYLES.gray) : null;
                  const isPlanning = group.planning;

                  return (
                    <div key={id} onClick={() => onSelectModule(id)}
                      style={{
                        background: '#fff',
                        borderRadius: 12,
                        border: '1px solid #e8e8e8',
                        cursor: 'pointer',
                        transition: 'box-shadow 0.15s, transform 0.15s',
                        display: 'flex',
                        flexDirection: 'column',
                        overflow: 'hidden',
                        minHeight: isPlanning ? 100 : 136,
                      }}
                      onMouseEnter={e => {
                        e.currentTarget.style.boxShadow = '0 6px 20px rgba(0,0,0,0.09)';
                        e.currentTarget.style.transform = 'translateY(-2px)';
                      }}
                      onMouseLeave={e => {
                        e.currentTarget.style.boxShadow = 'none';
                        e.currentTarget.style.transform = 'none';
                      }}>

                      {/* Card body */}
                      <div style={{ padding: '20px 14px 16px', display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', flex: 1 }}>

                        {/* Icon with badge overlay */}
                        <div style={{ position: 'relative', marginBottom: 12 }}>
                          <div style={{
                            width: 72, height: 72, borderRadius: 18,
                            background: mod.color + '18',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            fontSize: id === 'AI Advisor' ? 30 : 42,
                            color: id === 'AI Advisor' ? mod.color : undefined,
                            fontWeight: id === 'AI Advisor' ? 700 : undefined,
                          }}>
                            {id === 'Schedule' ? (() => {
                              const now = new Date();
                              const day = now.getDate();
                              const month = now.toLocaleString('en', { month: 'short' }).toUpperCase();
                              return (
                                <div style={{ width: 46, height: 46, borderRadius: 12, overflow: 'hidden', boxShadow: '0 2px 8px rgba(0,0,0,0.18), 0 1px 2px rgba(0,0,0,0.1)', display: 'flex', flexDirection: 'column', border: '1px solid rgba(0,0,0,0.08)' }}>
                                  <div style={{ background: 'linear-gradient(180deg, #ff3b30 0%, #d92d20 100%)', height: 16, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                    <span style={{ color: '#fff', fontSize: 9, fontWeight: 700, letterSpacing: 1 }}>{month}</span>
                                  </div>
                                  <div style={{ background: '#fff', flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', paddingBottom: 2 }}>
                                    <span style={{ color: '#1c1c1e', fontSize: 20, fontWeight: 300, lineHeight: 1, fontFamily: '-apple-system, BlinkMacSystemFont, sans-serif' }}>{day}</span>
                                  </div>
                                </div>
                              );
                            })() : id === 'Daily Log' ? (
                              <svg width="44" height="44" viewBox="0 0 44 44" fill="none" xmlns="http://www.w3.org/2000/svg">
                                {/* Back card */}
                                <rect x="9" y="6" width="28" height="28" rx="7" fill="#8b5cf6" opacity="0.25"/>
                                {/* Front card */}
                                <rect x="7" y="10" width="28" height="28" rx="7" fill="#8b5cf6"/>
                                {/* Checkmark */}
                                <path d="M16 24.5l4.5 4.5 8-9" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
                              </svg>
                            ) : mod.icon}
                          </div>
                          {badge && (
                            <span style={{
                              position: 'absolute', top: -6, right: -10,
                              fontSize: 10, padding: '2px 7px', borderRadius: 999,
                              fontWeight: 700, background: badge.bg, color: badge.color,
                              whiteSpace: 'nowrap',
                            }}>
                              {stats!.badge}
                            </span>
                          )}
                        </div>

                        {/* Title */}
                        <div style={{ fontSize: 13, fontWeight: 700, color: '#374151', marginBottom: 3 }}>
                          {mod.title}
                        </div>


                        {/* Progress bar */}
                        {stats?.progress != null && (
                          <div style={{ width: '100%', height: 4, background: '#f3f4f6', borderRadius: 2, marginTop: 10, overflow: 'hidden' }}>
                            <div style={{ width: `${stats.progress}%`, height: '100%', background: mod.color, borderRadius: 2, transition: 'width 0.5s' }} />
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>

      {/* Floating AI Chat */}
      {showAIAssistant && (
        <>
          <button onClick={() => setChatOpen(!chatOpen)}
            style={{ position: 'fixed', bottom: 28, right: 28, width: 56, height: 56, borderRadius: '50%', background: '#4f46e5', color: '#fff', border: 'none', cursor: 'pointer', fontSize: 22, boxShadow: '0 4px 20px rgba(79,70,229,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 900 }}>
            {chatOpen ? '✕' : '✦'}
          </button>
          {chatOpen && (
            <div style={{ position: 'fixed', bottom: 96, right: 28, width: chatWidth, height: chatHeight, background: '#fff', borderRadius: 16, boxShadow: '0 8px 40px rgba(0,0,0,0.18)', display: 'flex', flexDirection: 'column', zIndex: 900, overflow: 'hidden', border: '1px solid #e5e7eb' }}>
              <div style={{ background: '#4f46e5', padding: '10px 14px', display: 'flex', alignItems: 'center', gap: 10 }}>
                <div style={{ width: 30, height: 30, borderRadius: '50%', background: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 15, color: '#fff', flexShrink: 0 }}>✦</div>
                <div style={{ minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: '#fff', fontSize: 14, fontWeight: 600 }}>
                    {L.projectHub.aiAssistantTitle}
                    <span style={{ width: 7, height: 7, borderRadius: '50%', background: '#22c55e' }} />
                  </div>
                  <div style={{ color: 'rgba(255,255,255,0.7)', fontSize: 12, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{project.name}</div>
                </div>
                <button onClick={() => setChatExpanded(v => !v)}
                  title={chatExpanded ? L.projectHub.chatCollapse : L.projectHub.chatExpand}
                  style={{ marginLeft: 'auto', width: 28, height: 28, borderRadius: 8, border: 'none', background: 'rgba(255,255,255,0.15)', color: '#fff', cursor: 'pointer', fontSize: 15, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}
                  onMouseEnter={e => (e.currentTarget.style.background = 'rgba(255,255,255,0.28)')}
                  onMouseLeave={e => (e.currentTarget.style.background = 'rgba(255,255,255,0.15)')}>
                  {chatExpanded ? '⤡' : '⤢'}
                </button>
              </div>
              <div style={{ flex: 1, overflowY: 'auto', padding: 12, display: 'flex', flexDirection: 'column', gap: 10 }}>
                {messages.map((msg, i) => (
                  <div key={i} style={{ display: 'flex', justifyContent: msg.role === 'user' ? 'flex-end' : 'flex-start' }}>
                    <div style={{ maxWidth: '88%', padding: '9px 13px', borderRadius: msg.role === 'user' ? '14px 14px 4px 14px' : '14px 14px 14px 4px', background: msg.role === 'user' ? '#4f46e5' : '#f3f4f6', color: msg.role === 'user' ? '#fff' : '#374151', fontSize: 14, lineHeight: 1.55 }}>
                      {msg.role === 'user' ? msg.content : (
                        <ReactMarkdown
                          components={{
                            h2: ({children}) => <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 8, marginTop: 4, color: '#111' }}>{children}</div>,
                            h3: ({children}) => <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 6, marginTop: 10, color: '#374151' }}>{children}</div>,
                            p: ({children}) => <div style={{ marginBottom: 6 }}>{children}</div>,
                            strong: ({children}) => <span style={{ fontWeight: 700 }}>{children}</span>,
                            ul: ({children}) => <ul style={{ paddingLeft: 18, margin: '4px 0' }}>{children}</ul>,
                            li: ({children}) => <li style={{ marginBottom: 3 }}>{children}</li>,
                            hr: () => <div style={{ borderTop: '1px solid #e5e7eb', margin: '10px 0' }} />,
                            code: ({children}) => <code style={{ background: '#e5e7eb', borderRadius: 4, padding: '2px 6px', fontSize: 13, fontFamily: 'monospace' }}>{children}</code>,
                            pre: ({children}) => <pre style={{ background: '#1e1e2e', color: '#cdd6f4', borderRadius: 8, padding: '12px', fontSize: 13, overflowX: 'auto', margin: '8px 0' }}>{children}</pre>,
                            table: ({children}) => <table style={{ borderCollapse: 'collapse', width: '100%', margin: '8px 0', fontSize: 13 }}>{children}</table>,
                            th: ({children}) => <th style={{ background: '#ede9fe', color: '#4f46e5', padding: '6px 10px', border: '1px solid #ddd6fe', textAlign: 'left' }}>{children}</th>,
                            td: ({children}) => <td style={{ padding: '5px 10px', border: '1px solid #e5e7eb' }}>{children}</td>,
                            blockquote: ({children}) => <div style={{ borderLeft: '3px solid #4f46e5', paddingLeft: 12, color: '#6b7280', fontStyle: 'italic', margin: '6px 0' }}>{children}</div>,
                          }}
                        >{msg.content}</ReactMarkdown>
                      )}
                    </div>
                  </div>
                ))}
                {thinking && <div style={{ display: 'flex' }}><div style={{ padding: '10px 14px', borderRadius: '14px 14px 14px 4px', background: '#f3f4f6', fontSize: 13, color: '#9ca3af' }}>{L.projectHub.thinking}</div></div>}
                <div ref={messagesEndRef} />
              </div>
              {messages.length <= 1 && (
                <div style={{ padding: '0 12px 8px', display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  {L.projectHub.quickQuestions.map((q: string) => (
                    <button key={q} onClick={() => setInput(q)} style={{ fontSize: 11, padding: '4px 10px', borderRadius: 999, border: '1px solid #e5e7eb', background: '#fff', color: '#6b7280', cursor: 'pointer' }}>{q}</button>
                  ))}
                </div>
              )}
              <div style={{ padding: 10, borderTop: '1px solid #f3f4f6', display: 'flex', gap: 8, alignItems: 'flex-end' }}>
                <textarea
                  style={{ flex: 1, padding: '9px 12px', borderRadius: 10, border: '1px solid #e5e7eb', fontSize: 14, outline: 'none', fontFamily: 'inherit', resize: 'none', lineHeight: 1.5, maxHeight: 120, overflowY: 'auto' }}
                  placeholder={L.projectHub.chatPlaceholder}
                  value={input}
                  rows={1}
                  onChange={e => {
                    setInput(e.target.value);
                    e.target.style.height = 'auto';
                    e.target.style.height = Math.min(e.target.scrollHeight, 120) + 'px';
                  }}
                  onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(); } }}
                />
                <button onClick={sendMessage} disabled={!input.trim() || thinking} style={{ width: 38, height: 38, borderRadius: 10, background: input.trim() ? '#4f46e5' : '#e5e7eb', color: '#fff', border: 'none', cursor: input.trim() ? 'pointer' : 'not-allowed', fontSize: 18, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>→</button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default ProjectHub;
